import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  FileText, 
  Calculator, 
  User, 
  CreditCard,
  ChevronRight,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import { motion } from "framer-motion";

// Services data
const services = [
  {
    icon: FileText,
    title: "Individual Tax Preparation",
    description: "Comprehensive tax preparation for individuals and families, ensuring maximum refunds and full IRS compliance.",
    features: [
      "W-2 and 1040 tax filing",
      "Itemized deductions analysis",
      "Tax credit optimization",
      "E-filing with tracking",
      "Refund maximization strategies"
    ],
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    link: "/intake"
  },
  {
    icon: User,
    title: "Self-Employed & 1099 Tax Services",
    description: "Specialized tax services for freelancers, contractors, and self-employed professionals.",
    features: [
      "Quarterly tax estimates",
      "Business expense deductions",
      "Home office deductions",
      "Self-employment tax planning",
      "Schedule C preparation"
    ],
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    link: "/intake"
  },
  {
    icon: Calculator,
    title: "Business Tax Preparation",
    description: "Expert tax solutions for small businesses, partnerships, LLCs, and corporations.",
    features: [
      "Business entity tax returns",
      "Multi-state tax filing",
      "Payroll tax compliance",
      "Quarterly financial reviews",
      "Tax strategy planning"
    ],
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    link: "/intake"
  },
  {
    icon: CreditCard,
    title: "Credit Repair",
    description: "Professional credit repair services to help you improve your credit score and financial health.",
    features: [
      "Credit report analysis",
      "Dispute resolution assistance",
      "Credit score improvement strategies",
      "Debt management guidance",
      "Financial planning support"
    ],
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    link: "/credit-repair"
  },
  {
    icon: CreditCard,
    title: "Business Funding",
    description: "Access to business funding solutions to help grow your company and manage cash flow.",
    features: [
      "Business loan assistance",
      "Line of credit options",
      "Equipment financing",
      "Working capital solutions",
      "Funding application support"
    ],
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    link: "/funding"
  }
];

const whyChooseUs = [
  "IRS-Compliant Filing Process",
  "Secure Client Portal & Document Upload",
  "Personalized One-on-One Service",
  "Year-Round Support (Not Seasonal Only)",
  "Transparent Pricing — No Hidden Fees",
  "Nationwide Service — All 50 States"
];

const Services = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="pt-[68px]">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#121417] via-[#121417] to-[#152942] text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
                Our <span className="text-[#C9A227]">Services</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
                Professional tax preparation and financial services tailored to your needs. 
                We serve individuals, families, self-employed professionals, and small businesses nationwide.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-t-4 border-[#C9A227]">
                    <CardContent className="p-8">
                      <div className={`w-16 h-16 ${service.bgColor} rounded-lg flex items-center justify-center mb-6`}>
                        <service.icon className={`${service.color} w-8 h-8`} />
                      </div>
                      
                      <h3 className="text-2xl font-heading font-bold text-[#121417] mb-4">
                        {service.title}
                      </h3>
                      
                      <p className="text-slate-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      
                      <div className="space-y-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <CheckCircle className="text-[#C9A227] w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Link to={service.link}>
                        <Button 
                          className="w-full bg-[#121417] hover:bg-[#152942] text-white font-semibold group"
                        >
                          Get Started
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#121417] mb-4">
                Why Choose <span className="text-[#C9A227]">WMJ Enterprise Services</span>
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                We combine professional expertise with personalized service to deliver exceptional results for every client.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyChooseUs.map((reason, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex items-start gap-3 p-6 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <CheckCircle className="text-[#C9A227] w-6 h-6 flex-shrink-0 mt-1" />
                  <span className="text-[#121417] font-medium">{reason}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-[#121417] to-[#152942] py-20 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-slate-200 mb-8 leading-relaxed">
              Let our experienced team handle your tax preparation so you can focus on what matters most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/intake">
                <Button 
                  size="lg" 
                  className="bg-[#C9A227] hover:bg-[#B89120] text-[#121417] font-bold text-lg px-8 py-6"
                >
                  Start My Tax Intake
                  <ChevronRight className="ml-2" />
                </Button>
              </Link>
              <Link to="/">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold text-lg px-8 py-6"
                >
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Services;
